import { fetchAPI, safeFetchAPI } from '@/api';
import { capturePosthogEvent, log, logger, utils } from '@/helpers';
import { Setter } from '@/legacy-types';
import {
	CampaignBuilderSteps,
	CampaignEditingMethod,
	CampaignFormBuilderStepKeys,
	CampaignFormBuilderStepType,
	cleanCampaignObject,
	getCampaignEditingMethodType,
	getCampaignChannels,
	getCampaignTargeting,
	getDefaultCoMarketing,
	isVariantCampaign,
	validateSingleCampaign,
} from '@/pages/campaign-builder/campaign.builder.utils';
import {
	Campaign,
	CampaignChannelType,
	CampaignChannelUnion,
	CampaignMessageCaps,
	CampaignSchedule,
	CampaignsType,
	CampaignsV2Response,
	CampaignTargeting,
	CreateSponsoredCoMarketingTemplateArgs,
} from '@/types';
import { cloneDeep, isEqual } from 'lodash';
import { create } from 'zustand';
import { useCoMarketingStore } from './useCoMarketingStore';
import { useCampaignsStore } from './useCampaignsStore';
import { useCoMarketingInstallerEditStore } from './useCoMarketingInstallerEditStore';
import { useCoMarketingSponsorEditStore } from './useCoMarketingSponsorEditStore';
import { useMaximizeStore } from '../useMaximizeStore';
import { useSponsoringTemplatesStore } from './useSponsoringTemplatesStore';

export interface CampaignBuilderStoreState {
	loaded: boolean;
	loading: boolean;
	uid: string;

	editingMethod: CampaignEditingMethod | undefined;

	// The campaign being edited at any given time
	campaign?: Partial<Campaign>;
	// The original version of that campaign
	ogCampaign?: Partial<Campaign> | undefined;

	// For when the campaign above is an edit request campaign
	// We store the original campaign here
	// So if (editingMethod === CampaignEditingMethod.INSTALLER_EDITING_INSTALLED) {
	// Then the campaign above is the installers edit request campaign
	// And the campaign below is the original campaign
	// And if (editingMethod === CampaignEditingMethod.SPONSOR_EDITING_INSTALLED) {
	// Then the campaign above is the sponsors edit request campaign
	// And the campaign below is the original campaign
	userCampaign?: Partial<Campaign> | undefined;

	isPreview: boolean;

	selectedChannel: CampaignChannelType | undefined;
	selectedVariant: string | undefined;

	isDisabled: boolean;
	isCopying: boolean;
	step: CampaignFormBuilderStepType;

	template?: Campaign;
}

interface CampaignBuilderStoreActions {
	init: (resp: CampaignsV2Response, isPreview?: boolean) => void;

	setCampaign: (template?: Setter<Partial<Campaign | undefined>>) => void;
	setOgCampaign: (template?: Setter<Partial<Campaign | undefined>>) => void;

	setSelectedChannel: (channel: CampaignChannelType | undefined) => void;
	setSelectedVariant: (variant: string | undefined) => void;

	getCampaignChannel: <T = CampaignChannelUnion>(channel: CampaignChannelType, variant?: string) => T | undefined;
	setCampaignChannel: <T = CampaignChannelUnion>(template: Setter<Partial<T>>, channel: CampaignChannelType | undefined) => void;

	setCampaignSchedule: (schedule: Partial<CampaignSchedule & { enableQueue?: boolean }>, channel?: CampaignChannelType) => void;
	setCampaignMessageCaps: (caps: Partial<CampaignMessageCaps>, channel?: CampaignChannelType) => void;
	setCampaignTargeting: (targeting: Setter<Partial<CampaignTargeting>>, channel?: CampaignChannelType) => void;

	setIsCoMarketing: (isCoMarketing: boolean) => void;

	applyEditRequestCampaign: (editingMethod: CampaignEditingMethod) => void;

	hasChanged: () => boolean;

	setStep: (step: CampaignFormBuilderStepType) => void;
	nextStep: () => void;
	prevStep: () => void;
	goToStep: (step: CampaignFormBuilderStepType) => void;

	setTemplate: (template?: Campaign) => void;
	saveCampaign: (props?: SaveCampaignProps) => Promise<{ campaign: Campaign | undefined; error: string | undefined }>;
	reset: () => void;
}

export type CampaignBuilderStore = CampaignBuilderStoreState & CampaignBuilderStoreActions;

const initialState: CampaignBuilderStoreState = {
	loaded: false,
	loading: true,
	uid: utils.uid,
	editingMethod: undefined,
	campaign: undefined,
	ogCampaign: undefined,
	isPreview: false,
	selectedChannel: undefined,
	selectedVariant: undefined,
	isDisabled: false,
	isCopying: false,
	step: 'channels',
	userCampaign: undefined,
	template: undefined,
};

type SaveCampaignProps = {
	isExternalEditor?: boolean;
};

export const useCampaignBuilderStore = create<CampaignBuilderStore>((set, get) => ({
	...initialState,

	setTemplate(template?: Campaign) {
		const cleanedTemplate: Partial<Campaign> = {
			...template,
			audiences: ['all'],
			userID: utils.uid,
			id: '',
			agencyID: '',
			tags: [],
			timezone: '',
		};

		get().setCampaign((c) => ({ ...c, ...cleanedTemplate }));
		get().setOgCampaign((c) => ({ ...(c ?? {}), ...cleanedTemplate }));
		set({ template });
	},

	init: (resp: CampaignsV2Response, isPreview = false) => {
		const { campaign, sponsoredTemplate, installedTemplate } = resp;

		if (campaign.id && !getStepFromQuery(true)) {
			set({ step: 'review' });
		} else {
			const step = getStepFromQuery();
			set({ step });
		}

		const isCopying = new URLSearchParams(window.location.search).get('copy');
		if (isCopying) {
			campaign.name = `${campaign.name} (Copy)`;
			campaign.clonedFrom = campaign.id;
			set({ isCopying: true });
		}

		const editingMethod = getCampaignEditingMethodType(campaign, installedTemplate, sponsoredTemplate);

		const firstChannel = getCampaignChannels(campaign, true)[0]?.[0];

		// If campaign has channels in use, set the first one as the selected channel (If no selected channel)
		if (!get().selectedChannel && firstChannel) {
			set({ selectedChannel: firstChannel });
		}

		set({
			campaign,
			ogCampaign: cloneDeep(campaign),
			userCampaign: cloneDeep(campaign),
			loaded: true,
			loading: false,
			isPreview,
			editingMethod,
		});
		logger.log('init campaign').add('editingMethod', editingMethod).add('campaign', campaign);

		// Initialize the co-marketing store using the response
		useCoMarketingStore.getState().init(resp);
	},

	// Apply an edit request campaign to the campaign builder store
	applyEditRequestCampaign: (editingMethod: CampaignEditingMethod) => {
		if (
			editingMethod === CampaignEditingMethod.INSTALLER_EDITING_INSTALLED || // Installer is editing the installed campaign
			editingMethod === CampaignEditingMethod.REVIEW_INSTALLER_CHANGES // Sponsor is reviewing the installer's changes
		) {
			const installerStore = useCoMarketingInstallerEditStore.getState();
			const requestedCampaign = installerStore.ogEventData?.campaign;
			if (requestedCampaign) {
				set({
					campaign: cloneDeep(requestedCampaign),
					ogCampaign: cloneDeep(requestedCampaign),
				});
			}
			logger.log('applyEditRequestCampaign').add('editingMethod', editingMethod).add('requestedCampaign', requestedCampaign);
		}

		if (
			editingMethod === CampaignEditingMethod.SPONSOR_EDITING_INSTALLED || // Sponsor is editing the installed campaign
			editingMethod === CampaignEditingMethod.REVIEW_SPONSOR_CHANGES // Installer is reviewing the sponsor's changes
		) {
			const sponsorStore = useCoMarketingSponsorEditStore.getState();
			const requestedCampaign = sponsorStore.ogEventData?.campaign;
			if (requestedCampaign) {
				set({
					campaign: cloneDeep(requestedCampaign),
					ogCampaign: cloneDeep(requestedCampaign),
				});
			}
			logger.log('applyEditRequestCampaign').add('editingMethod', editingMethod).add('requestedCampaign', requestedCampaign);
		}

		set({ editingMethod });
		useCoMarketingStore.getState().applyEditRequestCoMarketing(editingMethod);
	},

	hasChanged: () => {
		const { campaign, ogCampaign } = get();
		return !isEqual(campaign, ogCampaign);
	},

	setOgCampaign: (template?: Partial<Campaign>) => {
		// if (get().editingMethod === CampaignEditingMethod.INSTALLER_EDITING_INSTALLED) {
		// 	useCoMarketingInstallerEditStore.getState().setOgCampaign(template);
		// 	return;
		// }

		// if (get().editingMethod === CampaignEditingMethod.SPONSOR_EDITING_INSTALLED) {
		// 	useCoMarketingSponsorEditStore.getState().setOgCampaign(template);
		// 	return;
		// }

		set({ ogCampaign: template });
	},

	setCampaign: (campaign) => {
		const currCampaign = get().campaign;
		const nextCampaign = typeof campaign === 'function' ? campaign(currCampaign) : campaign;

		logger.log('set campaign').add('nextCampaign', nextCampaign).add('editingMethod', get().editingMethod);

		// if (get().editingMethod === CampaignEditingMethod.INSTALLER_EDITING_INSTALLED) {
		// 	// Check if they already have an edit request
		// 	useCoMarketingInstallerEditStore.getState().setCampaign(nextCampaign);
		// 	return;
		// }

		// if (get().editingMethod === CampaignEditingMethod.SPONSOR_EDITING_INSTALLED) {
		// 	useCoMarketingSponsorEditStore.getState().setCampaign(nextCampaign);
		// 	return;
		// }

		set({ campaign: nextCampaign });
	},

	setSelectedChannel: (channel) => {
		const query = new URLSearchParams(window.location.search);
		query.set('channel', channel ?? '');
		window.history.replaceState({}, '', `${window.location.pathname}?${query.toString()}`);

		set({ selectedChannel: channel });
	},

	setSelectedVariant: (variant) => {
		const query = new URLSearchParams(window.location.search);
		query.set('variant', variant ?? '');
		window.history.replaceState({}, '', `${window.location.pathname}?${query.toString()}`);
		set({ selectedVariant: variant });
	},

	getCampaignChannel: <T = CampaignChannelUnion>(channel: CampaignChannelType, variant?: string) => {
		const currCampaign = get().campaign;

		const isVariant = isVariantCampaign(currCampaign);
		if (isVariant) {
			const useVariant = variant ?? get().selectedVariant;
			if (!useVariant) {
				// utils?.toaster?.addToast?.({ toastKey: 'cmp-chan-err', type: 'danger', message: 'No variant selected' });
				// Select the first variant ID
				const firstVariant = currCampaign?.abVariants?.[0];
				if (firstVariant) {
					get().setSelectedVariant(firstVariant.id);
				}
				return undefined;
			}

			if (channel !== CampaignChannelType.Email && channel !== CampaignChannelType.Text) {
				utils?.toaster?.addToast?.({ toastKey: 'cmp-chan-err', type: 'danger', message: 'Must be text/email' });
				return undefined;
			}

			const currAbVariant = currCampaign?.abVariants;
			const currCampaignChannel = currAbVariant?.find((v) => v.id === useVariant)?.[channel] ?? {};
			if (!currCampaignChannel) {
				utils?.toaster?.addToast?.({ toastKey: 'cmp-chan-err', type: 'danger', message: 'Variant not found' });
				return undefined;
			}
			return currCampaignChannel as T;
		}

		return currCampaign?.[channel] as T | undefined;
	},

	setCampaignChannel: <T = CampaignChannelUnion>(template: Setter<Partial<T>>, channel: CampaignChannelType | undefined, variant?: string) => {
		const useChannel = channel ?? get().selectedChannel;
		if (!useChannel) {
			return utils?.toaster?.addToast?.({ toastKey: 'cmp-chan-err', type: 'danger', message: 'No channel selected' });
		}

		const currCampaign = get().campaign;
		const isVariant = isVariantCampaign(currCampaign);

		if (isVariant) {
			const useVariant = variant ?? get().selectedVariant;
			if (!useVariant) {
				return utils?.toaster?.addToast?.({ toastKey: 'cmp-chan-err', type: 'danger', message: 'No variant selected' });
			}

			// Must be texst/email
			if (useChannel !== CampaignChannelType.Email && useChannel !== CampaignChannelType.Text) {
				return utils?.toaster?.addToast?.({ toastKey: 'cmp-chan-err', type: 'danger', message: 'Must be text/email' });
			}

			const currAbVariant = currCampaign?.abVariants;
			const currCampaignChannel = currAbVariant?.find((v) => v.id === useVariant)?.[useChannel] ?? {};
			if (!currCampaignChannel) {
				return utils?.toaster?.addToast?.({ toastKey: 'cmp-chan-err', type: 'danger', message: 'Variant not found' });
			}

			const nextCampaignChannel = typeof template === 'function' ? template(currCampaignChannel as T) : template;
			(nextCampaignChannel as CampaignChannelUnion).inUse = true;
			const nextAbVariant = currAbVariant?.map((v) => (v.id === useVariant ? { ...v, [useChannel]: nextCampaignChannel } : v));
			get().setCampaign({ ...currCampaign, abVariants: nextAbVariant });
			return;
		}

		const currCampaignChannel = currCampaign?.[useChannel] ?? {};
		const nextCampaignChannel = typeof template === 'function' ? template(currCampaignChannel as T) : template;
		(nextCampaignChannel as CampaignChannelUnion).inUse = true;
		get().setCampaign({ ...currCampaign, [useChannel]: nextCampaignChannel });
	},

	setStep(step) {
		const query = new URLSearchParams(window.location.search);
		query.set('stage', step);
		window.history.replaceState({}, '', `${window.location.pathname}?${query.toString()}`);
		log('Setting step', step);
		set({ step });
	},

	nextStep() {
		const { loading, campaign, selectedChannel, selectedVariant, step, setStep, setSelectedChannel } = get();
		if (loading) return;

		capturePosthogEvent('Campaign Builder - Step Next', {
			campaignID: get().campaign?.id,
			step,
		});

		// The content step gets some special logic to handle the multiple channels & variants
		// IE clicking next from the content step should go to the next channel/variant until they are on the last one
		// IF they click "next" from the last one, we go thru normal flow (canGoNext)
		if (step === 'content' && selectedChannel) {
			if (isVariantCampaign(campaign)) {
				const variants = campaign?.abVariants ?? [];
				const currentIndex = variants.findIndex((v) => v.id === selectedVariant);
				const isLastVariant = currentIndex === variants.length - 1;
				if (!isLastVariant) {
					const nextVariant = variants[currentIndex + 1];
					if (nextVariant) {
						get().setSelectedVariant(nextVariant.id);
						return;
					}
				}
			} else {
				const channels = getCampaignChannels(get().campaign, true).map(([channel]) => channel);
				const currentIndex = channels.indexOf(selectedChannel);
				const isLastChannel = currentIndex === channels.length - 1;
				if (!isLastChannel) {
					const nextChannel = channels[currentIndex + 1];
					if (nextChannel) {
						setSelectedChannel(nextChannel);
						return;
					}
				}
			}
		}

		const [canGoNext, error] = CampaignBuilderSteps[step].canGoNext(get().campaign);
		if (!canGoNext) {
			utils?.toaster?.addToast?.({
				type: 'danger',
				message: error || 'Must complete all steps before moving on',
				toastKey: 'cmp-step-err',
			});
			return;
		}

		const availibleSteps = CampaignFormBuilderStepKeys.filter((step) => CampaignBuilderSteps[step].isShown?.(get().campaign));

		const index = availibleSteps.indexOf(step);
		if (index < availibleSteps.length - 1) {
			log('Setting next step', availibleSteps[index + 1]);
			setStep(availibleSteps[index + 1] as CampaignFormBuilderStepType);

			const firstChannel = getCampaignChannels(get().campaign, true)[0]?.[0];
			setSelectedChannel(get().selectedChannel ?? firstChannel ?? undefined);
		}
	},

	prevStep() {
		const { loading, campaign, selectedChannel, selectedVariant, step, setStep, setSelectedChannel } = get();
		if (loading) return;

		capturePosthogEvent('Campaign Builder - Step Previous', {
			campaignID: get().campaign?.id,
			step,
		});

		// The content step gets some special logic to handle the multiple channels & variants
		// IE clicking prev from the content step should go to the previous channel/variant until they are on the first one
		// IF they click "prev" from the first one, we go thru normal flow
		if (step === 'content' && selectedChannel) {
			if (isVariantCampaign(campaign)) {
				const variants = campaign?.abVariants ?? [];
				const currentIndex = variants.findIndex((v) => v.id === selectedVariant);
				const isFirstVariant = currentIndex === 0;
				if (!isFirstVariant) {
					const prevVariant = variants[currentIndex - 1];
					if (prevVariant) {
						get().setSelectedVariant(prevVariant.id);
						return;
					}
				}
			} else {
				const channels = getCampaignChannels(campaign, true).map(([channel]) => channel);
				const currentIndex = channels.indexOf(selectedChannel);
				const isFirstChannel = currentIndex === 0;
				if (!isFirstChannel) {
					const prevChannel = channels[currentIndex - 1];
					if (prevChannel) {
						setSelectedChannel(prevChannel);
						return;
					}
				}
			}
		}

		const availibleSteps = CampaignFormBuilderStepKeys.filter((step) => CampaignBuilderSteps[step].isShown?.(get().campaign));
		const index = availibleSteps.indexOf(step);
		if (index > 0) {
			log('Setting prev step', availibleSteps[index - 1]);
			setStep(availibleSteps[index - 1] as CampaignFormBuilderStepType);
			const firstChannel = getCampaignChannels(get().campaign, true)[0]?.[0];
			setSelectedChannel(get().selectedChannel ?? firstChannel ?? undefined);
		}
	},

	goToStep(step) {
		const { setStep, setSelectedChannel } = get();
		// Get the step index
		const availibleSteps = CampaignFormBuilderStepKeys.filter((step) => CampaignBuilderSteps[step].isShown?.(get().campaign));
		const index = availibleSteps.indexOf(step);
		if (index === -1) {
			return;
		}

		// If already on the step, don't do anything
		if (get().step === step) {
			return;
		}

		if (index === 0) {
			capturePosthogEvent('Campaign Builder - Step Reset', {
				campaignID: get().campaign?.id,
			});
			log('Setting step to channels');
			setStep('channels');
			const firstChannel = getCampaignChannels(get().campaign, true)[0]?.[0];
			setSelectedChannel(get().selectedChannel ?? firstChannel ?? undefined);
			return;
		}

		// Get the step right before the current step
		const prevStep = availibleSteps[index - 1];
		// Make sure they can go to the step
		const [canGoNext, error] = CampaignBuilderSteps?.[prevStep]?.canGoNext?.(get().campaign) ?? [false, 'Invalid step'];
		if (!canGoNext) {
			utils?.toaster?.addToast?.({
				type: 'danger',
				message: error || 'Must complete all steps before moving on',
				placement: 'top',
				toastKey: 'cmp-step-err',
			});
			return;
		}
		capturePosthogEvent('Campaign Builder - Step Selected', {
			campaignID: get().campaign?.id,
			step,
		});
		log('Setting step', step);
		setStep(step);
		const firstChannel = getCampaignChannels(get().campaign, true)[0]?.[0];
		setSelectedChannel(get().selectedChannel ?? firstChannel ?? undefined);
	},

	setIsCoMarketing: (isCoMarketing: boolean) => {
		const currCampaign = get().campaign;
		const wasCoMarketing = get().ogCampaign?.isCoMarketing;

		// Can't enable it if it's an ab campaign
		if (isCoMarketing && currCampaign?.abVariants?.length) {
			return utils?.toaster?.addToast?.({
				toastKey: 'cmp-chan-err',
				type: 'danger',
				message: 'Cannot change saved co-marketing campaign to non-co-marketing',
			});
		}
		const isSaved = !!currCampaign?.id;

		// If the campaign is saved & was co-marketing, we can't change it to non-co-marketing
		if (isSaved && wasCoMarketing && !isCoMarketing) {
			return utils?.toaster?.addToast?.({
				toastKey: 'cmp-chan-err',
				type: 'danger',
				message: 'Cannot change saved co-marketing campaign to non-co-marketing',
			});
		}

		// If the campaign is saved & was not co-marketing, we can't change it to co-marketing
		if (isSaved && !wasCoMarketing && isCoMarketing) {
			const fromRecipe = !!currCampaign?.recipe?.id;
			const isActiveOrHasSends = currCampaign?.isActive;
			const couldBeSketchy = fromRecipe && isActiveOrHasSends;

			if (!utils.isLocal() && couldBeSketchy) {
				return utils?.toaster?.addToast?.({
					toastKey: 'cmp-chan-err',
					type: 'danger',
					message: 'Cannot change saved non-co-marketing campaign to co-marketing',
				});
			}
		}

		const nextCampaign = { ...currCampaign, isCoMarketing };
		// Some fields are not allowed
		if (isCoMarketing) {
			nextCampaign.giftPoints = undefined;
			set({ editingMethod: CampaignEditingMethod.EDITING_SPONSORING });
		} else {
			set({ editingMethod: CampaignEditingMethod.DEFAULT });
		}

		capturePosthogEvent('Campaign Builder - Set Co-Marketing', {
			campaignID: get().campaign?.id,
			isCoMarketing,
		});

		get().setCampaign(nextCampaign);
	},

	setCampaignSchedule: (schedule: Partial<CampaignSchedule & { enableQueue?: boolean }>, channel = get().selectedChannel) => {
		const currCampaign = get().campaign;

		// If they are not including a channel, we apply to the campaign
		if (!channel) {
			get().setCampaign({ ...currCampaign, ...schedule });
			return;
		}

		// Do not allow enableQueue to be set to true if it's channel specific
		if (currCampaign?.enableQueue || schedule.enableQueue) {
			return utils?.toaster?.addToast?.({
				toastKey: 'cmp-chan-err',
				type: 'danger',
				message: 'Cannot enable queue for channel specific schedule',
			});
		}

		const currCampaignChannel = currCampaign?.[channel] ?? ({} as CampaignChannelUnion);
		const currCampaignChannelSchedule = currCampaignChannel?.channelConfig?.schedule ?? {};
		const nextCampaignChannelSchedule = { ...currCampaignChannelSchedule, ...schedule };
		get().setCampaign({
			...currCampaign,
			[channel]: {
				...currCampaignChannel,
				channelConfig: {
					...currCampaignChannel.channelConfig,
					schedule: nextCampaignChannelSchedule,
				},
			},
		});
	},

	setCampaignMessageCaps: (caps: Partial<CampaignMessageCaps>, channel = get().selectedChannel) => {
		const currCampaign = get().campaign;

		// If they are not including a channel, we apply to the campaign
		if (!channel) {
			get().setCampaign({ ...currCampaign, ...caps });
			return;
		}

		const currCampaignChannel = currCampaign?.[channel] ?? ({} as CampaignChannelUnion);
		const currCampaignChannelMessageCaps = currCampaignChannel?.channelConfig?.messageCaps ?? {};
		const nextCampaignChannelMessageCaps = { ...currCampaignChannelMessageCaps, ...caps };
		get().setCampaign({
			...currCampaign,
			[channel]: {
				...currCampaignChannel,
				channelConfig: {
					...currCampaignChannel.channelConfig,
					messageCaps: nextCampaignChannelMessageCaps,
				},
			},
		});
	},

	setCampaignTargeting: (targeting: Setter<Partial<CampaignTargeting>>, channel = get().selectedChannel) => {
		const currCampaign = get().campaign;

		// If they are not including a channel, we apply to the campaign
		if (!channel) {
			const currTargeting = getCampaignTargeting(currCampaign);
			const nextTargeting = typeof targeting === 'function' ? targeting(currTargeting ?? {}) : targeting;
			get().setCampaign({ ...currCampaign, ...nextTargeting });
			return;
		}

		const currCampaignChannel = currCampaign?.[channel] ?? ({} as CampaignChannelUnion);
		const currCampaignChannelTargeting = currCampaignChannel?.channelConfig?.targeting ?? {};
		const nextCampaignChannelTargeting = typeof targeting === 'function' ? targeting(currCampaignChannelTargeting ?? {}) : targeting;
		get().setCampaign({
			...currCampaign,
			[channel]: {
				...currCampaignChannel,
				channelConfig: {
					...currCampaignChannel.channelConfig,
					targeting: nextCampaignChannelTargeting,
				},
			},
		});
	},

	saveCampaign: async (props?: SaveCampaignProps) => {
		// Check if we are on failsafe, if so we wait to update
		const isFailsafe = await fetchAPI(`${location.origin}/isFailsafe`);
		if (utils.isAlpineMain() && isFailsafe) {
			utils?.toaster?.addToast?.({
				message: 'This action is currently unavailable. Please try again in 30-60 seconds. Do not refresh or you will lose your progess',
				type: 'danger',
			});
			return { campaign: undefined, error: 'This action is currently unavailable. Please try again in 30-60 seconds. Do not refresh or you will lose your progess' };
		}

		// TODO: Co-marketing implementation
		// 1. Check if campaign is from a co-marketing campaign
		// 2. If so, check if they require approval
		const isRequestingChanges = false;
		if (isRequestingChanges) {
			return { campaign: undefined, error: 'Campaign is requesting changes' };
		}

		const campaign = get().campaign ?? {};
		const isCopying = get().isCopying;

		if (isCopying) {
			campaign.clonedFrom = campaign.id;
			campaign.id = undefined;
			campaign.recipe = undefined;
			campaign.recipes = undefined;
			campaign.recipeUserID = undefined;
			campaign.archived = false;
			// TODO: remove new co-marketing fields
		}

		const isCreating = campaign.id === '-1';
		if (isCreating) {
			campaign.id = undefined;
		}

		const errors = validateSingleCampaign(campaign);
		if (errors.length) {
			for (const error of errors) {
				utils?.toaster?.addToast?.({
					message: error,
					type: 'danger',
					duration: 10000,
				});
			}
			return { campaign: undefined, error: errors.join('\n') };
		}

		const cleanedCampaign = cleanCampaignObject(campaign);

		let newCampaign: Campaign | undefined;

		// If this is a sponsored co-marketing campaign, we need to save it differently
		if (cleanedCampaign.isCoMarketing) {
			const coMarketing = useCoMarketingStore.getState().sponsoredTemplate ?? getDefaultCoMarketing();

			if (isCopying) {
				coMarketing.id = undefined;
				coMarketing.published = false;
				coMarketing.archived = false;
			}

			// Co-marketing campaigns can NOT be active
			cleanedCampaign.isActive = false;

			const [response, error] = await safeFetchAPI<CreateSponsoredCoMarketingTemplateArgs>('/co-marketing/sponsored/:uid', {
				method: 'PUT',
				payload: {
					campaign: cleanedCampaign,
					coMarketing,
				} as CreateSponsoredCoMarketingTemplateArgs,
			});
			if (error) {
				utils?.toaster?.addToast?.({
					message: error || 'Failed to save co-marketing campaign',
					type: 'danger',
					duration: 10000,
				});
				return { campaign: undefined, error: error };
			}
			newCampaign = response?.campaign;
			useCoMarketingStore.getState().setCoMarketing(response?.coMarketing);
			useCoMarketingStore.getState().setOgCoMarketing(cloneDeep(response?.coMarketing));

			if (get().editingMethod === CampaignEditingMethod.EDITING_SPONSORING) {
				useSponsoringTemplatesStore.getState().fetchTemplates();
			}
		} else {
			const [newCampaignResponse, error] = await safeFetchAPI<Campaign>('/campaigns/v2/:uid', {
				method: 'PUT',
				payload: {
					campaign: cleanedCampaign,
				},
			});
			if (error) {
				utils?.toaster?.addToast?.({
					message: error || 'Failed to save campaign',
					type: 'danger',
					duration: 10000,
				});
				return { campaign: undefined, error: error };
			}
			newCampaign = newCampaignResponse;
		}

		get().setCampaign(newCampaign);
		get().setOgCampaign(cloneDeep(newCampaign));
		useCampaignsStore.getState().setCampaign(newCampaign as CampaignsType);
		useMaximizeStore.getState().setConfig((config) => ({ ...config, confirmExit: false }), {
			maximizedKey: 'campaign-builder',
		});

		if (isCopying) {
			// Remove copy query param
			const url = new URL(window.location.href);
			url.searchParams.delete('copy');
			window.history.replaceState({}, '', url.href);
			set({ isCopying: false });
		}

		// IF they are not editing from campaign drawer, we need to update the URL
		if (!props?.isExternalEditor) {
			const query = new URLSearchParams(window.location.search);
			query.set('campaignID', newCampaign?.id ?? '');
			window.history.replaceState({}, '', `${window.location.pathname}?${query.toString()}`);
		}

		capturePosthogEvent('Campaign Builder - Save Campaign', {
			campaignID: newCampaign?.id,
			isCoMarketing: cleanedCampaign.isCoMarketing,
		});

		utils?.toaster?.addToast?.({
			message: `Saved campaign ${newCampaign?.id}!`,
			type: 'success',
		});

		return { campaign: newCampaign, error: undefined };
	},

	reset: () => {
		set(initialState);
		useCoMarketingStore.getState().reset();
		useCoMarketingInstallerEditStore.getState().reset();
		useCoMarketingSponsorEditStore.getState().reset();
		useCoMarketingInstallerEditStore.getState().reset();
	},
}));

function getStepFromQuery(noDefault = false): CampaignFormBuilderStepType | undefined {
	const query = new URLSearchParams(window.location.search);
	const step = query.get('stage') as CampaignFormBuilderStepType;
	log('Campaign builder step', step);
	if (step && CampaignFormBuilderStepKeys.includes(step)) {
		return step;
	}
	if (noDefault) {
		return undefined;
	}
	return 'channels';
}
